import {
  apply,
  disabled,
  required,
  RootFieldContext,
  SchemaPathTree,
  validate,
  ValidationError
} from '@angular/forms/signals';
import { TimeReportingType } from '@app/shared/types/models/shared/time-reporting-type';
import { ProjectTaskModel, TimeRangeModel, TimeReportFormModel, timeToSeconds } from './time-report-form.types';

const REQUIRED_MESSAGE = 'ui-forms-this-field-is-required';

/** Dialog state the work-time form rules depend on. */
export interface TimeReportSchemaContext {
  /** Manual / Range / Timer mode of the hosting dialog. */
  reportingType: () => TimeReportingType;
  /** A time-report admin creates the report and may pick its owner. */
  canSelectUser: () => boolean;
  /** Company / Project / Task come from a Task or an edited report and cannot be changed. */
  projectTaskLocked: () => boolean;
}

/**
 * Company → Project → Task chain: each level is required and stays disabled until its parent is chosen
 * (or for good while the chain is locked). Location is optional and always editable.
 */
export function projectTaskSchema(path: SchemaPathTree<ProjectTaskModel>, locked: () => boolean): void {
  // The report always needs a Project and a Task, also when they are locked (and so not validated) above.
  validate(path, ({ value }) =>
    !value().project || !value().task ? { kind: 'projectTaskRequired', message: REQUIRED_MESSAGE } : undefined
  );
  required(path.company, { message: REQUIRED_MESSAGE });
  required(path.project, { message: REQUIRED_MESSAGE });
  required(path.task, { message: REQUIRED_MESSAGE });
  disabled(path.company, { when: () => locked() });
  disabled(path.project, { when: ({ valueOf }) => locked() || !valueOf(path.company) });
  disabled(path.task, { when: ({ valueOf }) => locked() || !valueOf(path.project) });
}

/** From must be strictly before To; the error is shown on both time pickers. */
export function timeRangeSchema(path: SchemaPathTree<TimeRangeModel>): void {
  const invalidRange = ({ valueOf }: Pick<RootFieldContext<unknown>, 'valueOf'>): ValidationError | undefined =>
    timeToSeconds(valueOf(path.from)) < timeToSeconds(valueOf(path.to))
      ? undefined
      : { kind: 'invalidTimeRange', message: 'time-reports-form-modal-the-time-range-is-not-valid' };

  validate(path.from, invalidRange);
  validate(path.to, invalidRange);
}

/** Rules of the work-time report form shared by the regular and the manual-hours dialog. */
export function timeReportFormSchema(
  path: SchemaPathTree<TimeReportFormModel>,
  context: TimeReportSchemaContext
): void {
  disabled(path.userName);
  required(path.userId, { when: () => context.canSelectUser(), message: REQUIRED_MESSAGE });
  disabled(path.userId, { when: ({ valueOf }) => !valueOf(path.projectTask.company) });

  required(path.date, { message: REQUIRED_MESSAGE });

  disabled(path.manualTime, { when: () => context.reportingType() !== TimeReportingType.MANUAL });
  validate(path.manualTime, ({ value }) =>
    timeToSeconds(value()) === 0
      ? { kind: 'invalidManualTime', message: 'ui-forms-time-duration-is-required' }
      : undefined
  );

  disabled(path.timeRange, { when: () => context.reportingType() !== TimeReportingType.RANGE });
  apply(path.timeRange, timeRangeSchema);

  apply(path.projectTask, projectTaskPath => projectTaskSchema(projectTaskPath, context.projectTaskLocked));

  required(path.hourType, { message: REQUIRED_MESSAGE });
  required(path.comment, { message: 'errors.required' });
}
